
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Play, 
  Square, 
  RotateCcw, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Zap,
  Activity,
  ArrowRight,
  Code,
  Database,
  Globe,
  AlertTriangle
} from 'lucide-react';
import type { CustomAction } from '@/types/action';

interface TestStep {
  id: string;
  name: string;
  status: 'pending' | 'running' | 'success' | 'error';
  duration?: number;
  error?: string;
  details?: any;
}

interface TestExecutionRunnerProps {
  action: CustomAction;
  onTestComplete?: (result: any) => void;
}

export const TestExecutionRunner: React.FC<TestExecutionRunnerProps> = ({
  action,
  onTestComplete
}) => {
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState<string | null>(null);
  const [testSteps, setTestSteps] = useState<TestStep[]>([
    { id: 'validation', name: 'Validating Configuration', status: 'pending' },
    { id: 'connection', name: 'Establishing Connection', status: 'pending' },
    { id: 'request', name: 'Sending Request', status: 'pending' },
    { id: 'response', name: 'Processing Response', status: 'pending' },
    { id: 'verification', name: 'Verifying Results', status: 'pending' }
  ]);
  const [testResult, setTestResult] = useState<any>(null);
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  const updateStepStatus = (stepId: string, status: TestStep['status'], duration?: number, error?: string, details?: any) => {
    setTestSteps(prev => prev.map(step => 
      step.id === stepId 
        ? { ...step, status, duration, error, details }
        : step
    ));
  };

  const runTest = async () => {
    setIsRunning(true);
    setProgress(0);
    setTestResult(null);
    setLogs([]);
    
    // Reset all steps
    setTestSteps(prev => prev.map(step => ({ ...step, status: 'pending' })));
    
    addLog('Starting test execution...');

    try {
      // Step 1: Validation
      setCurrentStep('validation');
      updateStepStatus('validation', 'running');
      addLog('Validating action configuration...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      updateStepStatus('validation', 'success', 1000);
      addLog('✓ Configuration validation passed');
      setProgress(20);

      // Step 2: Connection
      setCurrentStep('connection');
      updateStepStatus('connection', 'running');
      addLog(`Establishing connection to ${action.endpoint}...`);
      await new Promise(resolve => setTimeout(resolve, 1500));
      updateStepStatus('connection', 'success', 1500);
      addLog('✓ Connection established successfully');
      setProgress(40);

      // Step 3: Request
      setCurrentStep('request');
      updateStepStatus('request', 'running');
      addLog(`Sending ${action.method} request...`);
      addLog(`Headers: ${Object.keys(action.headers || {}).length} headers configured`);
      await new Promise(resolve => setTimeout(resolve, 2000));
      updateStepStatus('request', 'success', 2000);
      addLog('✓ Request sent successfully');
      setProgress(70);

      // Step 4: Response
      setCurrentStep('response');
      updateStepStatus('response', 'running');
      addLog('Processing API response...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock response data
      const mockResponse = {
        status: 200,
        statusText: 'OK',
        headers: {
          'content-type': 'application/json',
          'x-response-time': '123ms',
          'x-rate-limit-remaining': '99'
        },
        data: {
          success: true,
          user: {
            id: 'user-12345',
            name: 'John Doe',
            email: 'john@example.com',
            profile: {
              avatar: 'https://avatar.url',
              preferences: ['email', 'sms']
            }
          },
          metadata: {
            timestamp: new Date().toISOString(),
            version: '2.1.0'
          }
        },
        responseTime: 2345
      };

      updateStepStatus('response', 'success', 1000, undefined, mockResponse);
      addLog('✓ Response received and parsed');
      addLog(`Status: ${mockResponse.status} ${mockResponse.statusText}`);
      addLog(`Response time: ${mockResponse.responseTime}ms`);
      setProgress(90);

      // Step 5: Verification
      setCurrentStep('verification');
      updateStepStatus('verification', 'running');
      addLog('Verifying response structure...');
      await new Promise(resolve => setTimeout(resolve, 800));
      updateStepStatus('verification', 'success', 800);
      addLog('✓ Response verification completed');
      setProgress(100);

      setTestResult({
        success: true,
        ...mockResponse
      });

      addLog('🎉 Test execution completed successfully!');
      onTestComplete?.(mockResponse);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      updateStepStatus(currentStep || 'unknown', 'error', undefined, errorMessage);
      addLog(`❌ Test failed: ${errorMessage}`);
      setTestResult({
        success: false,
        error: errorMessage
      });
    } finally {
      setIsRunning(false);
      setCurrentStep(null);
    }
  };

  const stopTest = () => {
    setIsRunning(false);
    setCurrentStep(null);
    addLog('🛑 Test execution stopped by user');
  };

  const resetTest = () => {
    setProgress(0);
    setTestResult(null);
    setLogs([]);
    setTestSteps(prev => prev.map(step => ({ ...step, status: 'pending' })));
  };

  const getStepIcon = (step: TestStep) => {
    switch (step.status) {
      case 'running':
        return <Activity className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStepBadgeVariant = (status: TestStep['status']) => {
    switch (status) {
      case 'running': return 'default';
      case 'success': return 'secondary';
      case 'error': return 'destructive';
      default: return 'outline';
    }
  };

  return (
    <div className="space-y-6">
      {/* Test Controls */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-blue-50/50 to-purple-50/50 backdrop-blur-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-lg font-semibold">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                <Zap className="h-5 w-5 text-white" />
              </div>
              Test Execution Runner
            </CardTitle>
            <div className="flex items-center gap-2">
              {!isRunning ? (
                <>
                  <Button onClick={runTest} className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700">
                    <Play className="h-4 w-4 mr-2" />
                    Run Test
                  </Button>
                  {testResult && (
                    <Button onClick={resetTest} variant="outline">
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reset
                    </Button>
                  )}
                </>
              ) : (
                <Button onClick={stopTest} variant="destructive">
                  <Square className="h-4 w-4 mr-2" />
                  Stop Test
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Progress Bar */}
          {(isRunning || testResult) && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-medium">{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}

          {/* Test Configuration Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-white/50 rounded-lg border border-white/60">
            <div className="flex items-center gap-3">
              <Globe className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-xs text-muted-foreground">Endpoint</p>
                <p className="text-sm font-medium truncate" title={action.endpoint}>
                  {action.endpoint}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Code className="h-5 w-5 text-purple-500" />
              <div>
                <p className="text-xs text-muted-foreground">Method</p>
                <Badge variant="outline" className="text-xs">
                  {action.method}
                </Badge>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Database className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-xs text-muted-foreground">Parameters</p>
                <p className="text-sm font-medium">
                  {action.parameters?.length || 0} configured
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Test Steps */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-semibold">
              <Activity className="h-5 w-5 text-blue-600" />
              Execution Steps
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {testSteps.map((step, index) => (
                <div
                  key={step.id}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                    step.status === 'running' 
                      ? 'bg-blue-50 border-blue-200' 
                      : step.status === 'success'
                      ? 'bg-green-50 border-green-200'
                      : step.status === 'error'
                      ? 'bg-red-50 border-red-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white border">
                      {getStepIcon(step)}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{step.name}</p>
                      {step.duration && (
                        <p className="text-xs text-muted-foreground">
                          Completed in {step.duration}ms
                        </p>
                      )}
                      {step.error && (
                        <p className="text-xs text-red-600">{step.error}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={getStepBadgeVariant(step.status)} className="text-xs">
                      {step.status}
                    </Badge>
                    {index < testSteps.length - 1 && step.status === 'success' && (
                      <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Test Logs */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-semibold">
              <Code className="h-5 w-5 text-purple-600" />
              Execution Logs
              <Badge variant="outline" className="ml-2 text-xs">
                {logs.length} entries
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-black/5 border rounded-lg p-3">
              <ScrollArea className="h-[300px]">
                <div className="space-y-1 font-mono text-xs">
                  {logs.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      <div className="text-center">
                        <Code className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No logs yet. Run a test to see execution details.</p>
                      </div>
                    </div>
                  ) : (
                    logs.map((log, index) => (
                      <div
                        key={index}
                        className={`p-1 rounded ${
                          log.includes('✓') 
                            ? 'text-green-700 bg-green-50' 
                            : log.includes('❌')
                            ? 'text-red-700 bg-red-50'
                            : log.includes('🎉')
                            ? 'text-blue-700 bg-blue-50'
                            : 'text-gray-700'
                        }`}
                      >
                        {log}
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Test Results */}
      {testResult && (
        <Card className={`border-0 shadow-sm ${
          testResult.success 
            ? 'bg-green-50/50 border-green-200' 
            : 'bg-red-50/50 border-red-200'
        }`}>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-3 text-lg font-semibold">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                testResult.success 
                  ? 'bg-green-500 text-white' 
                  : 'bg-red-500 text-white'
              }`}>
                {testResult.success ? (
                  <CheckCircle className="h-5 w-5" />
                ) : (
                  <AlertTriangle className="h-5 w-5" />
                )}
              </div>
              Test Results
              <Badge variant={testResult.success ? 'secondary' : 'destructive'}>
                {testResult.success ? 'Passed' : 'Failed'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {testResult.success ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-white/50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{testResult.status}</div>
                    <div className="text-xs text-muted-foreground">Status Code</div>
                  </div>
                  <div className="text-center p-3 bg-white/50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{testResult.responseTime}ms</div>
                    <div className="text-xs text-muted-foreground">Response Time</div>
                  </div>
                  <div className="text-center p-3 bg-white/50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">
                      {Object.keys(testResult.headers).length}
                    </div>
                    <div className="text-xs text-muted-foreground">Headers</div>
                  </div>
                  <div className="text-center p-3 bg-white/50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">JSON</div>
                    <div className="text-xs text-muted-foreground">Content Type</div>
                  </div>
                </div>
                
                <div className="bg-white/50 border rounded-lg p-3">
                  <h4 className="text-sm font-medium mb-2">Response Data Preview</h4>
                  <ScrollArea className="h-[150px]">
                    <pre className="text-xs font-mono text-gray-700">
                      {JSON.stringify(testResult.data, null, 2)}
                    </pre>
                  </ScrollArea>
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-red-900 mb-2">Test Failed</h3>
                <p className="text-red-700">{testResult.error}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
